export interface LyricLine {
  /** Seconds; `null` for unsynced (plain) lyrics. */
  time: number | null;
  text: string;
}

const TIME_TAG = /\[(\d{1,3}):(\d{1,2})(?:[.:](\d{1,3}))?\]/g;

/**
 * LRC → sorted lines. Handles several timestamps on one line
 * (`[00:12.00][01:30.50]chorus`), 2- or 3-digit fractions, and skips ID
 * tags (`[ar:…]`, `[offset:…]` — applied) and empty timed lines. Text with
 * no timestamps at all comes back as unsynced lines.
 */
export function parseLrc(content: string): LyricLine[] {
  const lines: LyricLine[] = [];
  let offset = 0;
  let synced = false;

  for (const raw of content.split(/\r?\n/)) {
    const offsetTag = raw.match(/^\[offset:\s*([+-]?\d+)\]/i);
    if (offsetTag) {
      offset = Number(offsetTag[1]) / 1000;
      continue;
    }
    if (/^\[[a-z]+:.*\]$/i.test(raw.trim())) continue;

    const times: number[] = [];
    TIME_TAG.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = TIME_TAG.exec(raw))) {
      const fraction = match[3] ? Number(match[3]) / 10 ** match[3].length : 0;
      times.push(Number(match[1]) * 60 + Number(match[2]) + fraction);
    }
    const text = raw.replace(TIME_TAG, "").trim();
    if (times.length > 0) {
      synced = true;
      for (const time of times) lines.push({ time, text });
    } else if (text) {
      lines.push({ time: null, text });
    }
  }

  if (!synced) return lines;
  return lines
    .filter((line): line is { time: number; text: string } => line.time !== null)
    .map((line) => ({ time: Math.max(0, line.time - offset), text: line.text }))
    .sort((a, b) => a.time - b.time);
}

/** Index of the line being sung at `seconds` (-1 before the first). */
export function activeLineIndex(lines: LyricLine[], seconds: number): number {
  let index = -1;
  for (let i = 0; i < lines.length; i++) {
    const time = lines[i]!.time;
    if (time === null || time > seconds) break;
    index = i;
  }
  return index;
}
